import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import ConfirmModal from "components/confirm-modal";
import { useHistory } from "react-router-dom";
import { openTabAndPassData } from "utils/newTab";
import { fetchOrchidAPI } from "utils/api";
import { reportProblem } from "utils/frontend_error_reporting";
import { getErrorDetail } from "components/utility/utils";
import { useUser } from "contexts/UserContext";
import { useAuthModals } from "contexts/auth-modals-context";
import { useOrchidPubNubState } from "contexts/pubnub/OrchidPubNubStateContext";
import styles from "./RequestDifferentTime.module.scss";
import { track } from "utils/amplitude";
import { ChatOperations } from "contexts/pubnub/pubnub.types";

const messageInitialState =
  "Hi I'm interested in booking a session with you. I would love to learn more about your availability.";

const AMPLITUDE_COMPONENT_NAME = "request-different-time-modal";

const RequestDifferentTime = ({
  show,
  onHide,
  orchidPro,
  amplitudePageName,
}) => {
  const { user } = useUser();
  const history = useHistory();
  const { openAuthModals } = useAuthModals();
  const { chatOperations, privateChats } = useOrchidPubNubState();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(messageInitialState);
  const [showRequestSentModal, setShowRequestSentModal] = useState(false);
  const [showCannotRequestModal, setShowCannotRequestModal] = useState(false);

  useEffect(() => {
    if (!chatOperations || !show) {
      return;
    }
    chatOperations.addPartner(orchidPro.cognito_sub);
  }, [orchidPro, chatOperations, show]);

  const sendDisabled = useMemo(() => {
    // TODO: ? not sure why if could be falsy, not dived deep
    const chat = privateChats?.get(orchidPro.cognito_sub);
    const messages = chat ? chat.messages : [];
    const last_messages = messages.slice(Math.max(messages.length - 3, 0));
    const sendDisabled =
      user?.user_type === "orchid_pro"
        ? false
        : last_messages.length === 3 &&
          last_messages.every((m) => m.sender === user?.sub);
    return sendDisabled;
  }, [orchidPro.cognito_sub, privateChats, user?.sub, user?.user_type]);

  const sendMessage = useCallback(
    async (chatOperations: ChatOperations) => {
      if (sendDisabled) {
        setShowCannotRequestModal(true);
        onHide();
        setMessage(messageInitialState);
      } else {
        try {
          await chatOperations.sendMessage(orchidPro.cognito_sub, message);
          setShowRequestSentModal(true);
          onHide();
          setMessage(messageInitialState);
        } catch (err) {
          console.error("failed to send message", err);
        }
      }
    },
    [sendDisabled, message, onHide, orchidPro.cognito_sub],
  );
  const viewChat = useCallback(() => {
    if (user?.user_type === "orchid_pro") {
      openTabAndPassData(`/pro-dashboard/messages/${orchidPro.cognito_sub}`, {
        activeContactsTab: "professional",
      });
    } else {
      history.push(`/member-dashboard/messages/${orchidPro.cognito_sub}`);
    }
  }, [history, orchidPro.cognito_sub, user?.user_type]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setBusy(true);
      if (user?.signup_form_finished) {
        try {
          const response = await fetchOrchidAPI("/api/reporting/v1/event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event_name: "requestAppointment",
              event_tags: {
                orchid_pro: orchidPro.cognito_sub,
              },
            }),
          });
          if (!response.ok) {
            // we report this but still try to continue
            await reportProblem({
              reporter_module: "requestAppointment",
              context: { func: "trackEvent" },
              frontend_error: `failed to track request sending: ${getErrorDetail(
                await response.text(),
              )}`,
            });
            setBusy(false);
          }
          await sendMessage(chatOperations);
          setBusy(false);
        } catch (err) {
          console.error(err);
        }
      } else {
        openAuthModals();
      }
    },
    [
      chatOperations,
      openAuthModals,
      orchidPro.cognito_sub,
      sendMessage,
      user?.signup_form_finished,
    ],
  );

  return (
    <>
      {/* request sent */}
      <ConfirmModal
        labelTitle={"Request sent!"}
        children={`You can view your message from your chat tab, Or click  "View Chat" button below to view your message now.`}
        labelCancel={"I'm good."}
        labelConfirm={"View Chat"}
        show={showRequestSentModal}
        onCancel={() => setShowRequestSentModal(false)}
        onConfirm={viewChat}
      />
      {/* cannot request */}
      <ConfirmModal
        labelTitle={"You can't request any more"}
        children={
          "You can't request a session with this provider any more because you have exceeded the maximum number of request/chat you can send per provider before they respond to you. Please wait for their response."
        }
        labelConfirm={"OK"}
        hideCancel={true}
        show={showCannotRequestModal}
        onCancel={() => setShowCannotRequestModal(false)}
        onConfirm={() => setShowCannotRequestModal(false)}
      />
      <ModalLayout
        size={"md"}
        show={show}
        onHide={() => {
          onHide();
          setMessage(messageInitialState);
        }}
        contentClassName={styles["send-request-modal__content"]}
        Header={"Request different time"}
        headerClass={styles["send-request-modal__header"]}
        bodyClass={styles["send-request-modal__body"]}
        Footer={
          <Button
            variant="primary"
            type="submit"
            disabled={busy}
            form="send-request"
            onClick={() =>
              track(
                amplitudePageName,
                [AMPLITUDE_COMPONENT_NAME, "send-request-button"],
                "click",
              )
            }
          >
            Send request{" "}
            {busy && (
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
                className="ml-3"
              />
            )}
          </Button>
        }
        footerClass={styles["send-request-modal__footer"]}
      >
        <p>
          You don't see a desired time slot? Send an Orchid message to this
          provider to request a different session time.
        </p>
        <Form id="send-request" onSubmit={handleSubmit}>
          <Form.Group controlId="message">
            <Form.Control
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onFocus={() =>
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "message-field"],
                  "focus",
                )
              }
            />
          </Form.Group>
        </Form>
      </ModalLayout>
    </>
  );
};

export default RequestDifferentTime;
